import * as RadixTabs from '@radix-ui/react-tabs';
import type { ReactNode } from 'react';
import { cn } from '@shared/lib/cn';

export interface TabItem {
  value: string;
  label: ReactNode;
  content: ReactNode;
  count?: number;
}

export interface TabsProps {
  items: readonly TabItem[];
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  /** Accessible name of the tab list. */
  label: string;
  variant?: 'underline' | 'segmented';
  className?: string;
}

/**
 * Radix tabs (automatic activation on arrow keys). The DirectionProvider at the app root makes
 * arrow keys follow the visual direction in RTL (design system §4.15).
 */
export function Tabs({ items, value, defaultValue, onValueChange, label, variant = 'underline', className }: TabsProps) {
  const rootProps: RadixTabs.TabsProps = { defaultValue: defaultValue ?? items[0]?.value };
  if (value !== undefined) rootProps.value = value;
  if (onValueChange) rootProps.onValueChange = onValueChange;
  return (
    <RadixTabs.Root {...rootProps} className={className}>
      <RadixTabs.List
        aria-label={label}
        className={cn(
          'flex gap-1 overflow-x-auto overflow-y-hidden',
          variant === 'underline' ? 'border-b border-border-subtle' : 'inline-flex rounded-full bg-surface-sunken p-1',
        )}
      >
        {items.map((item) => (
          <RadixTabs.Trigger
            key={item.value}
            value={item.value}
            className={cn(
              'inline-flex min-h-touch-min shrink-0 items-center gap-2 px-4 text-sm font-medium text-secondary transition-colors duration-micro',
              variant === 'underline'
                ? '-mb-px border-b-2 border-transparent hover:text-primary data-[state=active]:border-primary data-[state=active]:text-brand'
                : 'min-h-0 rounded-full py-1.5 data-[state=active]:bg-surface data-[state=active]:text-primary data-[state=active]:shadow-1',
            )}
          >
            {item.label}
            {item.count !== undefined ? (
              <span className="tabular rounded-full bg-surface-sunken px-2 text-xs text-muted">{item.count}</span>
            ) : null}
          </RadixTabs.Trigger>
        ))}
      </RadixTabs.List>
      {items.map((item) => (
        <RadixTabs.Content key={item.value} value={item.value} className="pt-4 outline-none">
          {item.content}
        </RadixTabs.Content>
      ))}
    </RadixTabs.Root>
  );
}
